/**
 * Prefetch delle pagine principali al login.
 * Chiamate fire-and-forget: non bloccano il redirect verso la Dashboard.
 * Popolano sia la cache backend (evita seconde query) sia la sessionStorage frontend
 * (rendering istantaneo alla prima apertura della pagina).
 */

import axios from "axios";
import { mutate } from "swr";
import { writeSessionCache } from "./sessionCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const opts = { withCredentials: true, timeout: 15000 };

/**
 * Pre-popola la cache SWR global con il valore già scaricato.
 * `swrKey` è il path relativo (es. `/ordini?year=2026&light=true`) — deve
 * coincidere con la key usata da useSWR() nella pagina.
 */
function primeSwr(swrKey, data) {
  try {
    mutate(swrKey, data, { revalidate: false });
  } catch { /* noop */ }
}

/**
 * Avvia il prefetch di Dashboard, Ordini, Payments e Interviews.
 * Ritorna immediatamente (fire-and-forget interno).
 */
export function prefetchAppPagesOnLogin(year) {
  if (!year) year = new Date().getFullYear();
  
  // === Dashboard: 10 endpoints paralleli, salva snapshot completo ===
  Promise.all([
    axios.get(`${API}/interviews/stats?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/interviews/urgent?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/payments/stats?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/payments/pending?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/board-notes?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/commesse-pose/clienti-da-posare?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/commesse-pose/date-posa-scadute?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/crono-ordini`, opts).catch(() => null),
  ]).then(([stats, urgent, payStats, pending, notes, cliPosa, dateSc, crono]) => {
    if (!stats?.data) return; // se stats non c'è, evita di salvare stato parziale
    writeSessionCache(`dashboard_state_${year}`, {
      interviewStats: stats.data,
      urgentData: urgent?.data || { warning: [], critical: [] },
      paymentStats: payStats?.data || { totale_clienti: 0, totale_contratti: 0, totale_incassato: 0, totale_da_incassare: 0 },
      pendingPayments: pending?.data || [],
      boardNotes: notes?.data || [],
      clientiDaPosare: cliPosa?.data || [],
      datePosaScadute: dateSc?.data || [],
      ordiniScaduti: crono?.data?.scaduti || [],
      // anomalie e followUp derivano da ordini + interviews (calcolo lato Dashboard) → lasciamo vuoti
      anomalieContrattuali: [],
      followUpClienti: [],
    });
  }).catch(() => {});
  
  // === Ordini/Contratti (ordini + fatturato + commesse-map) ===
  // Popola sia legacy sessionCache (per compatibilità con altre pagine) sia SWR
  // (per apertura istantanea di /ordini che usa useSWR).
  axios.get(`${API}/ordini?year=${year}&light=true`, opts)
    .then((res) => {
      writeSessionCache(`ordini_${year}`, res.data);
      primeSwr(`/ordini?year=${year}&light=true`, res.data);
    })
    .catch(() => {});
  axios.get(`${API}/payments/fatturato-mensile?year=${year}`, opts)
    .then((res) => {
      writeSessionCache(`ordini_fatturato_${year}`, res.data);
      primeSwr(`/payments/fatturato-mensile?year=${year}`, res.data);
    })
    .catch(() => {});
  axios.get(`${API}/commesse-pose?year=${year}&light=true`, opts)
    .then((res) => {
      const map = {};
      (res.data || []).forEach(c => { if (c.offer_id) map[c.offer_id] = c; });
      writeSessionCache(`ordini_commesse_map_${year}`, map);
      primeSwr(`/commesse-pose?year=${year}&light=true`, res.data || []);
    })
    .catch(() => {});
  
  // === Payments (payments + stats + morosi + commesse-pose light) ===
  Promise.all([
    axios.get(`${API}/payments?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/payments/stats?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/payments/morosi?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/commesse-pose?year=${year}&light=true`, opts).catch(() => null),
  ]).then(([payments, stats, morosi, commesse]) => {
    if (!payments?.data) return;
    writeSessionCache(`payments_${year}`, {
      payments: payments.data,
      stats: stats?.data || { totale_clienti: 0, totale_contratti: 0, totale_incassato: 0, totale_da_incassare: 0, totale_scaduto: 0 },
      morosi: morosi?.data || [],
      commesse: commesse?.data || [],
    });
  }).catch(() => {});
  
  // === Interviews (interviews light + stats + urgent + monthly-stats) ===
  Promise.all([
    axios.get(`${API}/interviews?year=${year}&light=true`, opts).catch(() => null),
    axios.get(`${API}/interviews/stats?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/interviews/urgent?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/interviews/monthly-stats?year=${year}`, opts).catch(() => null),
  ]).then(([interviews, stats, urgent, monthly]) => {
    if (!interviews?.data) return;
    writeSessionCache(`interviews_${year}`, {
      interviews: interviews.data,
      stats: stats?.data || { totale: 0, vinti: 0, persi: 0, spariti: 0, percentuale_vinti: 0, consegnati: 0, commerciali: {} },
      urgentData: urgent?.data || { warning: [], critical: [] },
      monthlyStats: monthly?.data || [],
    });
  }).catch(() => {});
  
  // === CommessePose (commesse-pose light + posatori + trasporti + articoli + magazzini + clientiPagamenti) ===
  Promise.all([
    axios.get(`${API}/commesse-pose?year=${year}&light=true`, opts).catch(() => null),
    axios.get(`${API}/posatori`, opts).catch(() => null),
    axios.get(`${API}/trasporti`, opts).catch(() => null),
    axios.get(`${API}/tipo-articoli`, opts).catch(() => null),
    axios.get(`${API}/pagamenti/clienti?year=${year}`, opts).catch(() => null),
    axios.get(`${API}/magazzini`, opts).catch(() => null),
  ]).then(([commesse, posatori, trasporti, articoli, clienti, magazzini]) => {
    if (!commesse?.data) return;
    writeSessionCache(`commesse_pose_${year}`, {
      commesse: commesse.data,
      posatori: posatori?.data || [],
      trasporti: trasporti?.data || [],
      tipoArticoli: articoli?.data || [],
      clientiPagamenti: clienti?.data || [],
      magazzini: magazzini?.data || [],
    });
  }).catch(() => {});
  
  // === Offers (attivi + aux). Gli "inconclusi" NON vengono prefetchati:
  // rimangono lazy-loaded al primo click sulla tab per non sprecare banda al login. ===
  Promise.all([
    axios.get(`${API}/offers?year=${year}&light=true&stato_filter=attivi`, opts).catch(() => null),
    axios.get(`${API}/interviews?year=${year}&light=true`, opts).catch(() => null),
    axios.get(`${API}/marketing-docs`, opts).catch(() => null),
    axios.get(`${API}/settings`, opts).catch(() => null),
  ]).then(([offers, interviews, docs, settings]) => {
    if (!offers?.data) return;
    writeSessionCache(`offers_attivi_${year}`, offers.data);
    writeSessionCache(`offers_aux_${year}`, {
      interviews: interviews?.data || [],
      marketingDocs: docs?.data || [],
      settings: settings?.data || { youtube_link: "" },
    });
    // Prime SWR cache: /interviews light è usato anche da /rilievo-misure
    if (interviews?.data) primeSwr(`/interviews?year=${year}&light=true`, interviews.data);
  }).catch(() => {});

  // === Rilievo Misure (light) ===
  axios.get(`${API}/rilievo-misure?year=${year}`, opts)
    .then((res) => primeSwr(`/rilievo-misure?year=${year}`, res.data || []))
    .catch(() => {});
}
